import { useCallback, useEffect, useState } from "preact/hooks";

/**
 * The panel's router — the dashboard's, with the base at `/`.
 *
 * Real URLs, not a hash: `/orders/ord_…` is a link an operator pastes into a ticket and a link
 * the back button understands. The cost is one rewrite rule on the host that serves this app
 * (`README.md`). No router library: pattern matching we do with `String.split` is not worth
 * 3–5 KB gzipped.
 */

export const ADMIN_SCREENS = [
  "wallets",
  "monitoring",
  "providers",
  "supply",
  "order-book",
  "prices",
  "promo-codes",
  "promo-app",
  "promo-dev",
  "contracts",
  "orders",
  "subscriptions",
  "node-keys",
  "accounts",
  "fund-report",
  "mcp-usage",
  "finance",
  "network",
  "pools",
  "alerts",
  "audit",
  "withdrawals",
  "own-supply",
  "own-delegations",
  "own-admission",
  "own-economics",
  "own-bandwidth",
  "own-suppliers",
  "security",
  "admins",
  "nile",
] as const;

export type AdminScreen = (typeof ADMIN_SCREENS)[number];

export const SCREEN_LABELS: Record<AdminScreen, string> = {
  wallets: "Wallets",
  monitoring: "Monitoring",
  providers: "Providers",
  supply: "Supply",
  "order-book": "Order book",
  prices: "Prices",
  "promo-codes": "Promo codes",
  "promo-app": "tenergy.app",
  "promo-dev": "tenergy.dev",
  contracts: "Contracts",
  orders: "Orders",
  subscriptions: "Subscriptions",
  "node-keys": "Node keys",
  accounts: "Accounts",
  "fund-report": "Fund report",
  "mcp-usage": "MCP usage",
  finance: "Finance",
  network: "Network",
  pools: "Provider pools",
  alerts: "Alerts",
  audit: "Journal",
  withdrawals: "Withdrawals",
  "own-supply": "Own pools",
  "own-delegations": "Delegations",
  "own-admission": "Admission",
  "own-economics": "Economics",
  "own-bandwidth": "Bandwidth",
  "own-suppliers": "Suppliers",
  security: "Security",
  admins: "Admins",
  nile: "Nile",
};

/** The second line of each sidebar item: what the operator finds there. */
export const SCREEN_HINTS: Record<AdminScreen, string> = {
  wallets: "Our addresses, balances",
  monitoring: "Live metrics, queues",
  providers: "Credit, top-up limits",
  supply: "Fund wallets, pools, JustLend",
  "order-book": "Ask ladder and its sources",
  prices: "Retail, floor, ladder, day parts",
  "promo-codes": "Fee codes, redemptions",
  "promo-app": "65,000 energy for 1 h, mainnet",
  "promo-dev": "Nile drops, 3 % code on mainnet",
  contracts: "B2B terms per account",
  orders: "Search, fills, chain trail",
  subscriptions: "Plans, reserve, billing",
  "node-keys": "Masked keys, last use",
  accounts: "Balances and ledger",
  "fund-report": "Demand and settlement",
  "mcp-usage": "Connects, calls, clients",
  finance: "Profit, balance, leakage, flow",
  network: "Window, demand, providers, leads",
  pools: "Free energy, recovery, node checks",
  alerts: "Failures, drift, providers",
  audit: "Every admin action, signed",
  withdrawals: "Customer payouts, checks",
  "own-supply": "Stake, fill, window, yield",
  "own-delegations": "What is out, reclaims due",
  "own-admission": "Own stake or venue, per order",
  "own-economics": "Revenue, cost, margin, 7 / 30 d",
  "own-bandwidth": "Budget, use, pace",
  "own-suppliers": "Stake from others, payouts",
  security: "Sessions, passkeys, confirm key",
  admins: "Who has access, roles, freeze",
  nile: "Testnet: orders, wallets, pools",
};

/**
 * Sidebar groups, in order. Every screen but `nile` appears in exactly one; `nile` is the environment switch pinned at
 * the sidebar's foot, by the address and Sign out (owner 2026-10-22: not a section). Every group
 * shows mainnet. A `collapsible` group is open only while one of its screens is the current one (owner 2026-10-20).
 */
export const SCREEN_GROUPS: ReadonlyArray<{ label: string; screens: readonly AdminScreen[]; collapsible: boolean }> = [
  { label: "Operate", screens: ["wallets", "monitoring", "providers", "alerts", "orders", "subscriptions", "node-keys", "accounts", "withdrawals"], collapsible: true },
  { label: "Supply & pricing", screens: ["supply", "order-book", "prices", "promo-codes", "contracts"], collapsible: true },
  // Own supply (owner 2026-10-20, own-supply-console-spec.md): our stake as a producer, next to Supply & pricing.
  {
    label: "Own supply",
    screens: ["own-supply", "own-delegations", "own-admission", "own-economics", "own-bandwidth", "own-suppliers"],
    collapsible: true,
  },
  { label: "Growth", screens: ["promo-app", "promo-dev"], collapsible: true },
  { label: "Reports", screens: ["finance", "network", "pools", "fund-report", "mcp-usage"], collapsible: true },
  // docs/product/admin-auth.md §9: the panel's own people and keys. Admins shows for admin and root only (shell.tsx).
  { label: "Access", screens: ["security", "admins", "audit"], collapsible: true },
];

/** The Nile page's tabs: the mainnet screens of the same names, pointed at the Nile API. */
export const NILE_TABS = ["orders", "wallets", "providers", "monitoring", "own-supply"] as const;
export type NileTab = (typeof NILE_TABS)[number];

export const DEFAULT_SCREEN: AdminScreen = "monitoring";

export interface AdminRoute {
  screen: AdminScreen;
  /** The second segment, where a screen has a detail view (an order id), or a tab (`/nile/orders`). */
  param: string | null;
  /** The third segment: a tab's detail view (`/nile/orders/<id>`). */
  detail: string | null;
}

function isScreen(value: string): value is AdminScreen {
  return (ADMIN_SCREENS as readonly string[]).includes(value);
}

export function parseRoute(pathname: string): AdminRoute {
  const segments = pathname.split("/").filter((part) => part !== "");
  const first = segments[0] ?? "";
  return {
    screen: isScreen(first) ? first : DEFAULT_SCREEN,
    param: segments[1] === undefined ? null : decodeURIComponent(segments[1]),
    detail: segments[2] === undefined ? null : decodeURIComponent(segments[2]),
  };
}

export function routeHref(screen: AdminScreen, param?: string, detail?: string): string {
  const tail =
    (param === undefined ? "" : `/${encodeURIComponent(param)}`) +
    (param === undefined || detail === undefined ? "" : `/${encodeURIComponent(detail)}`);
  return screen === DEFAULT_SCREEN && param === undefined ? "/" : `/${screen}${tail}`;
}

/** Same-tab, left-click, no modifier: ours. Everything else is the browser's. */
function isPlainClick(event: MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  );
}

export interface Router {
  route: AdminRoute;
  navigate(screen: AdminScreen, param?: string, detail?: string): void;
  onNavigate(event: MouseEvent, screen: AdminScreen, param?: string, detail?: string): void;
  /** The link a screen puts in `href` (a middle-click opens it); `routeHref` when absent. */
  hrefFor?(screen: AdminScreen, param?: string): string;
}

export function useRouter(): Router {
  const [path, setPath] = useState(() =>
    typeof window === "undefined" ? "/" : window.location.pathname,
  );

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((screen: AdminScreen, param?: string, detail?: string) => {
    const href = routeHref(screen, param, detail);
    if (window.location.pathname !== href) window.history.pushState(null, "", href);
    setPath(href);
    window.scrollTo(0, 0);
  }, []);

  const onNavigate = useCallback(
    (event: MouseEvent, screen: AdminScreen, param?: string, detail?: string) => {
      if (!isPlainClick(event)) return;
      event.preventDefault();
      navigate(screen, param, detail);
    },
    [navigate],
  );

  return { route: parseRoute(path), navigate, onNavigate };
}
